import Link from 'next/link';
import { type FC } from 'react';

import { ButtonBase, type ButtonProps, type LinkProps } from '@craft/ui/components/buttons';
import { Stack } from '@craft/ui/components/structural';
import { Tooltip } from '@craft/ui/components/tooltips';
import { Typography } from '@craft/ui/components/typography';
import { ConditionallyRender } from '@craft/ui/components/util';
import { exhaustiveBorderStyle, mergeSx } from '@craft/ui/util';

import { EvaluatorNotClearedBadge } from '@/src/components/atoms/NotClearedBadge/EvaluatorNotClearedBadge';
import { LearnerNotClearedBadge } from '@/src/components/atoms/NotClearedBadge/LearnerNotClearedBadge';
import { useSelectedRole, useUser } from '@/src/features/auth/hooks';
import { useHasFeatureFlag } from '@/src/hooks/featureFlags/useHasFeatureFlag';

import { CenterSlot } from './CenterSlot';
import { type AssignedProgramContentCardSlots, getTooltipText } from './helpers';
import { LeftSlot } from './LeftSlot';
import { RightSlot } from './RightSlot';

export interface AssignedProgramContentCardProps {
  readonly 'aria-label': string;
  readonly description?: null | string;
  readonly href?: LinkProps['href'];
  readonly id?: string;
  readonly isLevelLocked?: boolean;
  readonly learnerProfile?: {
    cleared?: boolean | null;
    userProfile: {
      firstName: string;
    };
  };
  readonly name: string;
  readonly onClick?: ButtonProps['onClick'];
  readonly slots?: AssignedProgramContentCardSlots;
}

/**
 * There are numerous accessibility implications that target this component and cause the code to be
 * less than straightforward to understand. The key understanding to takeway is why the component is
 * conditionally typed as {@link Link} or {@link Button}. This requires a layered understanding of
 * the accessibility needs for this component as well as an understanding of the {@link Link}
 * component's inner workings.
 *
 * This component does not have a true disabled state. This is because it is wrapped in a
 * {@link Tooltip}. When a user using keyboard navigation tries to focus on a disabled element, the
 * browser will ignore the element and move on to the next focusable element. This is a problem, as
 * the {@link Tooltip} is not present in the DOM until the target is either hovered or focused.
 * Since the user is using keyboard navigation, they will never be able to process a hover event on
 * the component, and so we must expose a way for the user to focus the {@link Tooltip} target. This
 * means we must be able to focus the {@link AssignedProgramContentCard} for AssignedLevels that are
 * locked.
 *
 * Locked `AssignedLevel`s should prevent navigation to the corresponding `AssignedLevel` page when
 * interacted with. This is a problem, as typically one would just disable a {@link Link} that
 * should not be usable. However, since we need the target to be focusable, this solution cannot be
 * used.
 *
 * We already have another use-case where we do not need to navigate on click, so we extend the
 * conditions for which the component is a {@link Button}. When we need to allow focus and prevent
 * navigation, or for when we have been provided an `onClick` and no `href` prop, we use the
 * {@link Button} component. This enables us to set the `href` prop to `undefined`, properly
 * preventing navigation.
 */
export const AssignedProgramContentCard: FC<AssignedProgramContentCardProps> = ({
  description,
  href,
  isLevelLocked,
  learnerProfile,
  name,
  slots,
  ...props
}) => {
  const clearedFeatureEnabled = useHasFeatureFlag.ClearedStatusUx();

  const { cleared, isEvaluator, isLearner } = useSelectedRole();

  const { firstName: currentUserFirstName } = useUser();

  const isNotClearedEvaluator = clearedFeatureEnabled ? cleared === false && isEvaluator : false;

  const isNotClearedLearner = clearedFeatureEnabled ? learnerProfile?.cleared === false : false;

  const tooltipText = getTooltipText(slots?.rightSlot);

  const evaluatorNotClearedBadge = isNotClearedEvaluator ? (
    <EvaluatorNotClearedBadge evaluatorFirstName={currentUserFirstName} isCurrentUser />
  ) : null;

  const learnerNotClearedBadge =
    isNotClearedLearner && !isLearner && learnerProfile ? (
      <LearnerNotClearedBadge learnerFirstName={learnerProfile.userProfile.firstName} />
    ) : null;

  return (
    <Tooltip arrow placement='top' title={tooltipText}>
      <ButtonBase
        {...props}
        component={isLevelLocked || !href ? 'button' : Link}
        data-testid='assigned-program-content-card'
        href={isLevelLocked ? undefined : href}
        sx={mergeSx(
          {
            color: 'unset',
            fontWeight: 'unset',
            height: 'auto',
            textAlign: 'unset',
            textTransform: 'unset',
            whiteSpace: 'unset',
          },
          t => ({
            ...exhaustiveBorderStyle(t, {
              color: isLevelLocked ? t.palette.action.disabled : t.palette.action.active,
              thickness: 1,
              useOutline: true,
            }),
            cursor: isLevelLocked ? 'not-allowed' : props.onClick || href ? 'pointer' : undefined,
            paddingX: t.spacing(1),
            paddingY: t.spacing(1),
            pointerEvents: !props.onClick && !href ? 'none' : 'auto',
          }),
        )}
      >
        <Stack gap={0.5} width='100%'>
          <Stack alignItems='center' direction='row' gap={1}>
            {slots?.centerSlot && <CenterSlot {...slots.centerSlot} />}
            <Stack gap={0.5} maxWidth='100%'>
              <Stack alignItems='center' direction='row' justifyContent='space-between'>
                <Typography fontSize='1rem'>{name}</Typography>
                <ConditionallyRender
                  show={learnerNotClearedBadge !== null || evaluatorNotClearedBadge !== null}
                >
                  <Stack alignItems='center' direction='row' gap={1}>
                    {learnerNotClearedBadge}
                    {evaluatorNotClearedBadge}
                  </Stack>
                </ConditionallyRender>
              </Stack>
              <Typography
                color='text.secondary'
                lineClamp={1}
                variant={description ? 'body2' : 'body2Italic'}
              >
                {description || 'No Description'}
              </Typography>
            </Stack>
          </Stack>
          {(slots?.leftSlot ?? slots?.rightSlot) && (
            <Stack
              alignItems='center'
              direction='row'
              gap={0.5}
              justifyContent={slots.leftSlot ? 'space-between' : 'flex-end'}
            >
              {slots.leftSlot && <LeftSlot {...slots.leftSlot} />}
              {slots.rightSlot && <RightSlot {...slots.rightSlot} />}
            </Stack>
          )}
        </Stack>
      </ButtonBase>
    </Tooltip>
  );
};
